// The Test pushes tab: four test fires at one device, by employee code.

import { useState } from 'react';
import api from '../../services/api';
import {
  inferDecoration,
  type BannerTab,
  type CelebrationType,
  type DecorationType,
  type MissedSubtype,
  type TestPushType,
} from './bannerOptions';
import type { PushLogEntry } from './pushLog';

export function useTestPushes({
  setSending,
  report,
}: {
  setSending: (on: boolean) => void;
  report: (entry: PushLogEntry, from: BannerTab) => void;
}) {
  const [empCode, setEmpCode] = useState('');
  const [pushType, setPushType] = useState<TestPushType>('random_attendance');
  const [slot, setSlot] = useState(1);
  // Random-attendance capture mode. On a real slot this is rolled per-employee
  // from the project's selfie percentage; here it's forced so both branches are
  // testable on demand.
  const [selfieRequired, setSelfieRequired] = useState(true);
  const [celebrationType, setCelebrationType] = useState<CelebrationType>('birthday');
  const [missedSubtype, setMissedSubtype] = useState<MissedSubtype>('no-attendance');
  const [anniversaryYears, setAnniversaryYears] = useState(1);
  const [festivalName, setFestivalName] = useState('Diwali');
  // Festival decoration for the Celebration type. Auto-follows the festival
  // name until the sender overrides it, so the common case needs no thought.
  const [festivalDecoration, setFestivalDecoration] = useState<DecorationType>(() =>
    inferDecoration('Diwali')
  );
  const [festivalDecorationTouched, setFestivalDecorationTouched] = useState(false);

  const canSend = empCode.trim().length > 0;

  const changeFestivalName = (name: string) => {
    setFestivalName(name);
    // Keep following the name until the sender picks manually.
    if (!festivalDecorationTouched) setFestivalDecoration(inferDecoration(name));
  };

  const pickFestivalDecoration = (d: DecorationType) => {
    setFestivalDecoration(d);
    setFestivalDecorationTouched(true);
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      let res;
      if (pushType === 'celebration') {
        const payload: any = { empCode: empCode.trim(), type: celebrationType };
        if (celebrationType === 'anniversary') payload.years = anniversaryYears;
        if (celebrationType === 'festival') {
          payload.festivalName = festivalName.trim();
          payload.decoration = festivalDecoration;
        }
        res = await api.post('/celebrations/test-push', payload);
      } else {
        const payload: any = { emp_code: empCode.trim(), type: pushType };
        if (pushType === 'random_attendance') {
          payload.slot = slot;
          payload.selfie_required = selfieRequired;
        }
        if (pushType === 'missed_attendance') payload.subtype = missedSubtype;
        res = await api.post('/attendance-slots/test-push', payload);
      }
      const data = res.data;

      report(
        {
          timestamp: new Date().toLocaleTimeString(),
          empCode: empCode.trim(),
          type: pushType,
          slot: pushType === 'random_attendance' ? slot : undefined,
          selfieRequired: pushType === 'random_attendance' ? selfieRequired : undefined,
          celebrationType: pushType === 'celebration' ? celebrationType : undefined,
          missedSubtype: pushType === 'missed_attendance' ? missedSubtype : undefined,
          success: data.success,
          message: data.pushResult?.success
            ? pushType === 'celebration'
              ? `Push sent. Card override active for 1 hour — refresh the mobile home to see it.`
              : 'Push sent successfully'
            : data.pushResult?.error || data.message || 'Unknown result',
          content: data.notificationContent ?? null,
          liveActivity: data.liveActivity ?? null,
          countdown: data.countdown ?? null,
          diagnostics: data.diagnostics ?? null,
        },
        'test'
      );
    } catch (err: any) {
      const errMsg = err.response?.data?.message || err.message || 'Request failed';
      report(
        {
          timestamp: new Date().toLocaleTimeString(),
          empCode: empCode.trim(),
          type: pushType,
          slot: pushType === 'random_attendance' ? slot : undefined,
          selfieRequired: pushType === 'random_attendance' ? selfieRequired : undefined,
          celebrationType: pushType === 'celebration' ? celebrationType : undefined,
          success: false,
          message: errMsg,
        },
        'test'
      );
    } finally {
      setSending(false);
    }
  };

  return {
    empCode,
    setEmpCode,
    pushType,
    setPushType,
    slot,
    setSlot,
    selfieRequired,
    setSelfieRequired,
    celebrationType,
    setCelebrationType,
    missedSubtype,
    setMissedSubtype,
    anniversaryYears,
    setAnniversaryYears,
    festivalName,
    changeFestivalName,
    festivalDecoration,
    pickFestivalDecoration,
    canSend,
    send,
  };
}

export type TestPushes = ReturnType<typeof useTestPushes>;
